/**
 * Module dependencies.
 */

import { FadeInUp } from 'components/atoms/in-view-once/fade-in-up';
import {
  Gallery,
  Image,
  StyledMdClose,
  StyledMdNavigateBefore,
  StyledMdNavigateNext,
  Wrapper,
} from './portfolio-slide-show.style';

import { InViewOnce } from 'components/atoms/in-view-once/in-view-once';
import { PortfolioCategory } from 'types/portfolio';
import { ReactNode, useContext, useMemo, useState } from 'react';
import { ThemeContext } from '@emotion/react';
import { color } from 'styles/utils/colors';
import { getAsset } from 'utils/resolve-admin';
import { resolveImageWidth, useViewport } from 'components/hooks/use-viewport';
import Lightbox, { SlideImage } from 'yet-another-react-lightbox';

/**
 * `Props` type.
 */

type Props = {
  currentCategory: string;
  data: PortfolioCategory | null;
};

/**
 * Export `PortfolioSlideShow` component.
 */

export function PortfolioSlideShow(props: Props): ReactNode {
  const { currentCategory, data } = props;
  const { width } = useViewport();
  const [index, setIndex] = useState<number | null>(null);
  const theme = useContext(ThemeContext);
  const images = useMemo((): SlideImage[] => {
    return (data?.images ?? []).map((image) => ({
      height: image.height,
      predominantColor: image.predominantColor,
      src: getAsset(image.filename_disk, { width: resolveImageWidth(width) }),
      width: image.width,
    }));
  }, [data?.images, width]);

  if (!data) {
    return null;
  }

  return (
    <>
      <Wrapper>
        <Gallery>
          {(data?.images ?? []).map((image, index) => (
            <InViewOnce
              AnimationComponent={FadeInUp}
              key={`${currentCategory}-${image.filename_disk}`}
            >
              <Image image={image} onClick={() => setIndex(index)} />
            </InViewOnce>
          ))}
        </Gallery>
      </Wrapper>

      <Lightbox
        close={() => setIndex(null)}
        index={index ?? 0}
        open={index !== null}
        render={{
          iconClose() {
            return <StyledMdClose />;
          },
          iconNext() {
            return <StyledMdNavigateNext />;
          },
          iconPrev() {
            return <StyledMdNavigateBefore />;
          },
        }}
        slides={images}
        styles={{
          container: {
            backgroundColor: color('primary900', 0.9)({ theme }),
          },
          navigationPrev: {
            color: 'white',
            height: '48px',
            width: '48px',
          },
        }}
      />
    </>
  );
}
